/**
 * Concrete, id-parameterized `BdCondition` subclasses used by the generic
 * (blueprint-driven) filter plugins. The base classes serialize `{ [id]: … }`
 * generically; these thin subclasses only expose a public constructor because
 * the base constructors are `protected`.
 *
 * Wire shapes are proven against the live `POST /clinics/qb-filter`:
 *   LIST  → { "<id>": { includes, excludes } }
 *   RANGE → { "<id>": { from, to } }
 *   FLAG  → { "<id>": { flag } }
 */
import type {
  FilterConditionDto,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdBooleanCondition,
  BdCondition,
  BdContentCondition,
  BdRangeCondition
} from '@bindoc-projects/condition-domain-lib';

export class GenericContentCondition extends BdContentCondition<string> {
  constructor(id: string, includes: string[] | null, excludes: string[] | null) {
    super(id, includes ?? [], excludes ?? []);
  }
}

export class GenericRangeCondition extends BdRangeCondition<number> {
  constructor(id: string, from: number | null, to: number | null) {
    super(id, from, to);
  }
}

export class GenericFlagCondition extends BdBooleanCondition {
  constructor(id: string, flag: boolean) {
    super(id, flag);
  }
}

/**
 * SUBSTRING condition → `{ "<id>": { value } }`. Proven against the live
 * qb-filter (`{ substring }` / `{ contains }` / `{ equals }` all 400; only
 * `{ value }` returns 200).
 */
export class GenericSubstringCondition extends BdCondition {
  constructor(
    id: string,
    public value: string | null,
  ) {
    super(id);
  }

  toJSON(): FilterConditionDto {
    return { [this.id]: { value: this.value } };
  }
}

/**
 * Wrapper (department / deqs) condition → `{ "<id>": { condition: <inner> } }`.
 * The inner tree is evaluated against ONE department / DeQS Leistungsbereich.
 * Wire shape proven against the live qb-filter (HTTP 200).
 */
export class GenericWrapperCondition extends BdCondition {
  constructor(
    id: string,
    private readonly inner: BdCondition | null,
  ) {
    super(id);
  }

  toJSON(): FilterConditionDto {
    return { [this.id]: { condition: this.inner ? this.inner.toJSON() : null } };
  }
}

/** Comparison operators accepted by the clinic operating-number condition. */
export type ClinicOperatingNumberComparison =
  | 'GT'
  | 'GTE'
  | 'LT'
  | 'LTE'
  | 'EQ';

/**
 * German wording for each comparison — used by the form's "Vergleich" select.
 * Single source of truth so the picker and the filter's summary text cannot
 * drift apart.
 */
export const CLINIC_OPERATING_NUMBER_COMPARISON_LABELS: {
  [comparison in ClinicOperatingNumberComparison]: string;
} = {
  LT: 'kleiner als',
  LTE: 'kleiner oder gleich',
  EQ: 'gleich',
  GTE: 'größer oder gleich',
  GT: 'größer als',
};

/**
 * Math symbol for each comparison — used in the compact summary / detail text,
 * where the whole condition tree is squeezed into one line.
 */
export const CLINIC_OPERATING_NUMBER_COMPARISON_SYMBOLS: {
  [comparison in ClinicOperatingNumberComparison]: string;
} = {
  LT: '<',
  LTE: '≤',
  EQ: '=',
  GTE: '≥',
  GT: '>',
};

/**
 * operatingNumber (Kennzahl) condition →
 * `{ "operatingNumber": { operatingNumberId, comparison, value } }`.
 * Comparison enum GT|GTE|LT|LTE|EQ proven against the live qb-filter
 * (word-forms GREATER/EQUAL → 400).
 */
export class GenericOperatingNumberCondition extends BdCondition {
  constructor(
    id: string,
    public operatingNumberId: string | null,
    public comparison: ClinicOperatingNumberComparison,
    public value: number | null,
  ) {
    super(id);
  }

  toJSON(): FilterConditionDto {
    return {
      [this.id]: {
        operatingNumberId: this.operatingNumberId,
        comparison: this.comparison,
        value: this.value,
      },
    };
  }
}
